import { useState, useEffect } from 'react'
import { Minus, Plus } from 'lucide-react'
import { formatQuantity, PLURAL_UNITS, PLURAL_TO_SINGULAR } from '../../lib/itemDefaults'
import type { AdjustReason } from '../../hooks/useAdjustQuantity'

type Props = {
  isOpen:     boolean
  itemName:   string
  currentQty: number
  unit:       string | null
  onClose:    () => void
  onConfirm:  (newTotal: number, reason: AdjustReason) => Promise<void>
}

export function QuickQuantitySheet({
  isOpen, itemName, currentQty, unit, onClose, onConfirm,
}: Props) {
  const [draft,  setDraft]  = useState(currentQty)
  const [reason, setReason] = useState<AdjustReason>('used')
  const [saving, setSaving] = useState(false)

  // Reset draft + reason whenever the sheet re-opens for a different item.
  useEffect(() => {
    if (isOpen) {
      setDraft(currentQty)
      setReason('used')
    }
  }, [isOpen, currentQty])

  if (!isOpen) return null

  const delta      = draft - currentQty
  const hasChange  = delta !== 0
  const canSave    = hasChange && !saving
  const draftLabel = formatQuantity(draft, unit) ?? '×0'

  // Normalize unit through the shared maps before deciding what to render
  // under the stepper. Avoids "bottless" / "unitss" double-pluralization.
  const rawUnit         = (unit ?? '').toLowerCase().trim()
  const singularUnit    = PLURAL_TO_SINGULAR[rawUnit] ?? rawUnit
  const showUnitLabel   = !!singularUnit && singularUnit !== 'count'
  const stepperUnitLabel = !showUnitLabel
    ? null
    : draft === 1
      ? singularUnit
      : PLURAL_UNITS[singularUnit] ?? (singularUnit.endsWith('s') ? singularUnit : `${singularUnit}s`)

  async function handleSave() {
    setSaving(true)
    try {
      // If the user picked a specific reason ('expired' / 'correction'), keep
      // it. Otherwise infer from delta direction: increase = bought, decrease = used.
      const inferredReason: AdjustReason =
        reason !== 'used' && reason !== 'bought'
          ? reason
          : delta > 0 ? 'bought' : 'used'
      await onConfirm(draft, inferredReason)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-[480px] rounded-t-[24px] bg-cream pb-8">
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1 w-10 rounded-full bg-border-avatar" />
        </div>

        <div className="px-5 pb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
            How much do you have?
          </div>
          <h2 className="font-serif text-[20px] font-normal leading-[1.15] tracking-[-0.015em] text-ink truncate">
            {itemName}
          </h2>
        </div>

        {/* Stepper */}
        <div className="px-5 mt-5 mb-5">
          <div className="flex items-center justify-center gap-5">
            <button
              type="button"
              onClick={() => setDraft(Math.max(0, draft - 1))}
              disabled={draft <= 0}
              aria-label="Decrease"
              className="h-12 w-12 rounded-full border border-border-sand bg-paper flex items-center justify-center text-ink disabled:opacity-30 disabled:cursor-not-allowed active:bg-cream-warm"
            >
              <Minus className="h-5 w-5" strokeWidth={2} />
            </button>

            <div className="flex flex-col items-center min-w-[80px]">
              <span className="font-serif text-[34px] font-medium text-ink leading-none tabular-nums">
                {draft}
              </span>
              {showUnitLabel && (
                <span className="font-serif text-[11px] italic text-ink-muted mt-1">
                  {stepperUnitLabel}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => setDraft(draft + 1)}
              aria-label="Increase"
              className="h-12 w-12 rounded-full bg-forest text-forest-ink flex items-center justify-center active:bg-forest/90"
            >
              <Plus className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>

          {hasChange && (
            <div className="text-center mt-3">
              <span className={`font-serif text-[12px] italic ${delta > 0 ? 'text-forest' : 'text-terracotta'}`}>
                {delta > 0 ? `+${delta}` : delta} from {formatQuantity(currentQty, unit) ?? '×0'}
              </span>
            </div>
          )}
        </div>

        {/* Reason chips — only show if there's a delta. Filtered by direction. */}
        {hasChange && (
          <div className="px-5 mb-5">
            <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted mb-2">
              Why?
            </div>
            <div className="flex gap-2 flex-wrap">
              {([
                { id: 'used',       label: delta > 0 ? null : 'Used some' },
                { id: 'bought',     label: delta > 0 ? 'Bought more' : null },
                { id: 'expired',    label: 'Expired' },
                { id: 'correction', label: 'Correction' },
              ] as const).filter(c => c.label).map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setReason(c.id as AdjustReason)}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ${
                    reason === c.id
                      ? 'bg-forest text-forest-ink'
                      : 'bg-paper border border-border-sand text-ink-muted'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-5 flex flex-col gap-2">
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="w-full rounded-[14px] bg-forest text-forest-ink py-3 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {saving ? 'Saving…' : hasChange ? `Save ${draftLabel}` : 'No change'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 font-serif text-[13px] italic text-ink-muted"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
